/**
 * Builds the scenes the motion column views draw (W2-04a; DESIGN.md §4.7, §5.6.7, §6.2, §6.6-6.9, ARCHITECTURE.md
 * §7.7). Everything is read from the document, the engine and the editor store, and translated here, so the render
 * code only draws.
 *
 *  - Curves come from the engine (`sampleChannel`: exactly what the export carries, Σ for 'sum'), through the shared
 *    curve cache keyed by (lane, engine.revision, step); the Scroll speed column draws `sampleEffectiveSpeed`, the
 *    speed that actually moves the notes (dH/dt of the active integrator), with the layer's keyframe values as a faint
 *    guide.
 *  - The value axis auto-fits the visible curves and keys (with property hints: opacity always shows 0..100 %,
 *    speed includes 0), unless the user locked it or a key is being dragged (frozen).
 *  - The opacity column hatches the beats where the line's notes are hidden: Σ opacity < 0, decided near zero with
 *    the engine's exact f32 line alpha (`lineState(...).alpha < 0`, DESIGN.md §6.2).
 *  - Badges: the easing (plain words in Guided, ids in Full), "ignored" (legacy speed easing Phira ignores),
 *    "baked" (eased speed baked on export in Classic mode), "≈ split" (approximate shape-preserving split in this
 *    session), text segment modes.
 * Scenes are cached per column and rebuilt only when their inputs changed.
 */

import { holdBeforeChartStart } from '../../core/tools/curveDisplay';
import { sampleShownSpeed } from '../../core/tools/shownSpeed';
import { Ease as EaseApi } from '../../core/ease/index';
import type { Ease } from '../../core/ease/types';
import { absentMessageKey } from '../../core/model/props';
import type { Key, KeyId, LineId, LineProp, TrackId } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import type { RGB } from '../../core/util/types';
import type { IrEvent, IrEventList } from '../../core/ir/types';
import type { Engine } from '../../core/engine/types';
import type { TimeAxis } from '../../render/types';
import { axisContext } from '../../render/timeline/axis';
import { beatSamplerFromSeconds, curveValueRange, type SampledCurve } from '../../render/timeline/curveCache';
import type {
  MotionColorBand,
  MotionColumnScene,
  MotionCurveLayer,
  MotionGhostKey,
  MotionKeyView,
  MotionLaneScene,
  MotionOverlayScene,
  MotionRawEvent,
  MotionSegmentBadge,
} from '../../render/timeline/motion/motionScene';
import { keyBeats } from '../../render/timeline/motion/motionHit';
import type { ColumnRef, DocStore, EditorStore, VersionTopic } from '../../state/types';
import { isLayered, keysInsideSpan, type ColumnSpec, type LaneSpec } from './columnSpec';
import {
  easeBadge,
  formatNumber,
  formatRgb,
  formatValue,
  isEased,
  joinList,
  quoteText,
  subLabelKey,
  textModeBadge,
  type PropContext,
  type Translate,
} from './format';
import { m } from './motion.messages';
import type { MotionSession, ValueRange } from './session';

// ---------------------------------------------------------------------------------------------------------------
// Small helpers shared with the commands and gestures
// ---------------------------------------------------------------------------------------------------------------

export function laneKeyOf(line: LineId, prop: LineProp, layer: number): string {
  return `${String(line)}:${prop}:${String(layer)}`;
}

/** The (line, prop, layer) a lane edits: Guided Σ views edit layer 0. */
export function laneRef(spec: ColumnSpec, lane: LaneSpec): ColumnRef {
  return { line: spec.line, prop: lane.prop, layer: spec.sumView ? 0 : lane.layer };
}

export function lineTopic(line: LineId): VersionTopic {
  return `line:${String(line)}` as VersionTopic;
}

export function laneTrack(store: Pick<DocStore, 'idx'>, ref: ColumnRef): TrackId | undefined {
  return store.idx.track(ref.line, ref.prop, ref.layer);
}

/** Numeric value of a track value (NaN for colours and strings). */
export function numericOf(v: unknown): number {
  return typeof v === 'number' ? v : Number.NaN;
}

function isRgb(v: unknown): v is RGB {
  return Array.isArray(v) && v.length === 3 && v.every((x) => typeof x === 'number');
}

/** Keys of a track, sorted by beat. */
export function trackKeys(store: Pick<DocStore, 'doc' | 'idx'>, track: TrackId | undefined): Key[] {
  if (track === undefined) return [];
  const out: Key[] = [];
  for (const id of store.idx.keysByTrack(track)) {
    const k = store.doc.keys.get(id);
    if (k !== undefined) out.push(k);
  }
  return out;
}

/** Playhead beat (f64): the transport's chart time through the tempo map. */
export function playheadBeat(session: MotionSession): number {
  const s = session.services;
  const store = s.store;
  if (store === null) return 0;
  const sec = chartTimeOf(s);
  const engine = s.engine;
  if (engine !== null) return engine.tempo.beatF64At(sec);
  return sec;
}

function chartTimeOf(s: MotionSession['services']): number {
  try {
    return s.audio.transport.chartTime();
  } catch {
    return 0;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Key views
// ---------------------------------------------------------------------------------------------------------------

interface KeyCacheEntry {
  readonly version: number;
  readonly selection: object;
  readonly views: readonly MotionKeyView[];
}
const keyCaches = new WeakMap<MotionSession, Map<TrackId, KeyCacheEntry>>();

export function keyViews(
  session: MotionSession,
  store: DocStore,
  track: TrackId | undefined,
  line: LineId,
): readonly MotionKeyView[] {
  if (track === undefined) return [];
  const sel = session.services.editor.getState().selection;
  const version = store.version(lineTopic(line));
  let cache = keyCaches.get(session);
  if (cache === undefined) {
    cache = new Map();
    keyCaches.set(session, cache);
  }
  const hit = cache.get(track);
  if (hit?.version === version && hit.selection === sel) return hit.views;
  const keys = trackKeys(store, track);
  const views: MotionKeyView[] = keys.map((k, i) => {
    const base = {
      id: k.id,
      beat: Beats.toF64(k.t),
      vIn: numericOf(k.vIn),
      vOut: numericOf(k.vOut),
      jump: i > 0 && !sameTrackValue(k.vIn, k.vOut),
      ease: k.ease,
      link: k.link,
      selected: sel.keys.has(k.id),
      segmentSelected: sel.segments.has(k.id),
    };
    if (isRgb(k.vIn) && isRgb(k.vOut)) return { ...base, rgbIn: k.vIn, rgbOut: k.vOut };
    if (typeof k.vIn === 'string' && typeof k.vOut === 'string') return { ...base, textIn: k.vIn, textOut: k.vOut };
    return base;
  });
  cache.set(track, { version, selection: sel, views });
  return views;
}

export function sameTrackValue(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => x === b[i]);
  return a === b;
}

// ---------------------------------------------------------------------------------------------------------------
// Curves
// ---------------------------------------------------------------------------------------------------------------

type CurveVariant = 'main' | 'sum' | 'effective' | 'guide';

function sampleCurve(
  session: MotionSession,
  engine: Engine,
  ref: ColumnRef,
  variant: CurveVariant,
  axis: TimeAxis,
  compact: boolean,
): SampledCurve {
  const [b0, b1] = axis.range;
  const layer: number | 'sum' = variant === 'sum' ? 'sum' : ref.layer;
  const sampleSec =
    variant === 'effective'
      ? (t0: number, t1: number, out: Float32Array) => {
          sampleShownSpeed(engine, ref.line, t0, t1, out);
        }
      : (t0: number, t1: number, out: Float32Array) => {
          engine.sampleChannel(ref.line, ref.prop, layer, t0, t1, out);
        };
  // Before second 0 the chart never plays: the value at beat 0 is shown there (UX-043).
  return holdBeforeChartStart(
    session.curves.sample(
      {
        track: `${laneKeyOf(ref.line, ref.prop, ref.layer)}|${variant}`,
        revision: engine.revision,
        beat0: b0,
        beat1: b1,
        pxPerBeat: axis.pxPerBeat,
        pxStep: compact ? 3 : 2,
      },
      beatSamplerFromSeconds(engine.tempo, sampleSec),
    ),
  );
}

/** Default value ranges per property (raw units), used when nothing is visible. */
const DEFAULT_RANGE: Readonly<Record<LineProp, readonly [number, number]>> = {
  moveX: [-675, 675],
  moveY: [-450, 450],
  rotate: [-180, 180],
  alpha: [0, 255],
  speed: [0, 10],
  scaleX: [0, 2],
  scaleY: [0, 2],
  color: [0, 255],
  text: [0, 1],
  incline: [-90, 90],
  paint: [0, 10],
  gif: [0, 1],
};

/** Smallest span of an auto-fitted axis (a flat curve sits in a readable band). */
const MIN_SPAN: Readonly<Record<LineProp, number>> = {
  moveX: 60,
  moveY: 60,
  rotate: 30,
  alpha: 255,
  speed: 4,
  scaleX: 0.5,
  scaleY: 0.5,
  color: 255,
  text: 1,
  incline: 20,
  paint: 10,
  gif: 1,
};

/** Auto-fitted value range of a lane: visible curves and key values, property hints, minimum span, 8 % padding. */
export function autoRange(
  prop: LineProp,
  curves: readonly SampledCurve[],
  keys: readonly MotionKeyView[],
  b0: number,
  b1: number,
): ValueRange {
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  const take = (v: number): void => {
    if (!Number.isFinite(v)) return;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  };
  for (const c of curves) {
    const r = curveValueRange(c, b0, b1);
    if (r !== null) {
      take(r[0]);
      take(r[1]);
    }
  }
  const beats = keyBeats(keys);
  for (let i = 0; i < keys.length; i++) {
    const b = beats[i] ?? 0;
    if (b < b0 || b > b1) continue;
    const k = keys[i];
    if (k === undefined) continue;
    take(k.vIn);
    take(k.vOut);
  }
  if (prop === 'alpha') {
    take(0);
    take(255);
  } else if (prop === 'speed' || prop === 'incline' || prop === 'paint') take(0);
  else if (prop === 'gif') {
    take(0);
    take(1);
  }
  if (lo > hi) {
    const d = DEFAULT_RANGE[prop];
    lo = d[0];
    hi = d[1];
  }
  const min = MIN_SPAN[prop];
  if (hi - lo < min) {
    const c = (lo + hi) / 2;
    lo = c - min / 2;
    hi = c + min / 2;
    if (prop === 'speed' || prop === 'paint') {
      // Keep 0 visible without centring a positive-only property around it.
      if (lo < 0 && (keys.every((k) => !(k.vOut < 0)) || keys.length === 0)) {
        hi -= lo;
        lo = 0;
      }
    }
  }
  const pad = (hi - lo) * 0.08;
  return { lo: lo - pad, hi: hi + pad };
}

/**
 * Auto-fit with hysteresis: the axis grows at once when the visible values need more room, and shrinks only when
 * they use less than 60 % of it, so it does not twitch while the view scrolls (playback follow, wheel).
 */
export function stableRange(prev: ValueRange | undefined, next: ValueRange): ValueRange {
  if (prev === undefined) return next;
  const inside = next.lo >= prev.lo && next.hi <= prev.hi;
  return inside && next.hi - next.lo >= 0.6 * (prev.hi - prev.lo) ? prev : next;
}

// ---------------------------------------------------------------------------------------------------------------
// Lane scenes
// ---------------------------------------------------------------------------------------------------------------

/** Beats where the line's notes are hidden by a negative opacity (Σ < 0, exact near zero). */
function hiddenBands(
  engine: Engine,
  line: LineId,
  sum: SampledCurve,
  b0: number,
  b1: number,
): Array<readonly [number, number]> {
  const out: Array<readonly [number, number]> = [];
  let start: number | null = null;
  const n = sum.values.length;
  const half = sum.step / 2;
  for (let i = 0; i < n; i++) {
    const beat = sum.beat0 + i * sum.step;
    const v = sum.values[i] ?? Number.NaN;
    let hidden = v < -1e-3;
    if (!hidden && Number.isFinite(v) && Math.abs(v) <= 1e-3) {
      try {
        hidden = engine.lineState(line, engine.tempo.secAtBeatF64(beat)).alpha < 0;
      } catch {
        hidden = false;
      }
    }
    if (hidden && start === null) start = beat - half;
    if (!hidden && start !== null) {
      out.push([Math.max(b0, start), Math.min(b1, beat - half)]);
      start = null;
    }
  }
  if (start !== null) out.push([Math.max(b0, start), b1]);
  return out.filter(([s, e]) => e > s);
}

function lerpRgb(a: RGB, b: RGB, y: number): RGB {
  const ch = (i: 0 | 1 | 2): number => Math.min(255, Math.max(0, a[i] + (b[i] - a[i]) * y));
  return [ch(0), ch(1), ch(2)];
}

const WHITE: RGB = [255, 255, 255];

/** The colour over the visible range: per segment pieces about 3 px tall (progress in seconds, like Phira). */
function colorBands(engine: Engine | null, keys: readonly MotionKeyView[], axis: TimeAxis): MotionColorBand[] {
  const [b0, b1] = axis.range;
  const out: MotionColorBand[] = [];
  if (keys.length === 0) return out;
  const beats = keyBeats(keys);
  const first = keys[0] as MotionKeyView;
  if (first.beat > b0) out.push({ beat0: b0, beat1: Math.min(first.beat, b1), rgb: WHITE });
  const sec = (b: number): number => (engine === null ? b : engine.tempo.secAtBeatF64(b));
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i] as MotionKeyView;
    const n = keys[i + 1];
    const start = beats[i] ?? 0;
    const end = n === undefined ? b1 : n.beat;
    if (end < b0 || start > b1) continue;
    const a = k.rgbOut ?? WHITE;
    if (n === undefined || k.ease.kind === 'hold') {
      out.push({ beat0: Math.max(start, b0), beat1: Math.min(end, b1), rgb: a });
      continue;
    }
    const b = n.rgbIn ?? a;
    const px = (end - start) * axis.pxPerBeat;
    const pieces = Math.max(1, Math.min(256, Math.ceil(px / 3)));
    const s0 = sec(start);
    const s1 = sec(end);
    for (let j = 0; j < pieces; j++) {
      const p0 = start + ((end - start) * j) / pieces;
      const p1 = start + ((end - start) * (j + 1)) / pieces;
      if (p1 < b0 || p0 > b1) continue;
      const mid = (p0 + p1) / 2;
      const prog = s1 > s0 ? (sec(mid) - s0) / (s1 - s0) : 0;
      const y = EaseApi.modelY(k.ease, Math.min(1, Math.max(0, prog)));
      out.push({ beat0: p0, beat1: p1, rgb: lerpRgb(a, b, Number.isFinite(y) ? y : 0) });
    }
  }
  return out;
}

function rawEvents(engine: Engine, ref: ColumnRef, format: (v: number) => string, t: Translate): MotionRawEvent[] {
  let list: IrEventList<unknown> | readonly [] | undefined;
  try {
    const ir = engine.irLine(ref.line);
    if (isLayered(ref.prop)) {
      const layer = ir.eventLayers[ref.layer] ?? null;
      list =
        layer === null
          ? undefined
          : (layer[`${ref.prop}Events` as keyof typeof layer] as IrEventList<unknown> | undefined);
    } else if (ir.extended !== null && ir.extended !== 'absent') {
      list = ir.extended[`${ref.prop}Events` as keyof typeof ir.extended] as
        IrEventList<unknown> | readonly [] | undefined;
    }
  } catch {
    list = undefined;
  }
  if (list === undefined) return [];
  const show = (v: unknown): string =>
    typeof v === 'number' ? format(v) : typeof v === 'string' ? quoteText(t, v) : isRgb(v) ? formatRgb(t, v) : '?';
  return (list as readonly IrEvent<unknown>[]).map((e) => {
    const easing =
      e.bezier !== 0
        ? t(m.rawBezier, { p: e.bezierPoints.map((x) => formatNumber(x, 3)).join(', ') })
        : e.easingLeft !== 0 || e.easingRight !== 1
          ? t(m.rawEasingSlice, {
              id: e.easingType,
              l: formatNumber(e.easingLeft, 3),
              r: formatNumber(e.easingRight, 3),
            })
          : t(m.rawEasing, { id: e.easingType });
    const notes: string[] = [];
    switch (e.origin) {
      case 'padding':
        notes.push(t(m.rawPadding));
        break;
      case 'single':
        notes.push(t(m.rawSingle));
        break;
      case 'trailing':
        notes.push(t(m.rawTrailing));
        break;
      case 'lead':
        notes.push(t(m.rawLead));
        break;
      case 'bakePiece':
        notes.push(t(m.rawBakePiece));
        break;
      case 'segment':
        break;
    }
    if (e.linkgroup !== 0) notes.push(t(m.rawLink, { n: e.linkgroup }));
    return {
      beat0: Beats.tripleToF64(e.startTime),
      beat1: Beats.tripleToF64(e.endTime),
      values: t(m.rawValues, { from: show(e.start), to: show(e.end) }),
      easing,
      note: notes.length === 0 ? null : joinList(t, notes),
    };
  });
}

interface LaneInputs {
  readonly session: MotionSession;
  readonly store: DocStore;
  readonly engine: Engine | null;
  readonly editor: EditorStore;
  readonly spec: ColumnSpec;
  readonly lane: LaneSpec;
  readonly axis: TimeAxis;
  readonly laneWidth: number;
  readonly compact: boolean;
}

function buildLane(inp: LaneInputs): MotionLaneScene {
  const { session, store, engine, editor, spec, lane, axis, compact } = inp;
  const t: Translate = (k, p) => session.services.i18n.t(k, p);
  const ref: ColumnRef = { line: spec.line, prop: lane.prop, layer: spec.sumView ? 0 : lane.layer };
  const lk = laneKeyOf(ref.line, ref.prop, ref.layer);
  const track = laneTrack(store, ref);
  const keys = keyViews(session, store, track, spec.line);
  const pctx: PropContext = { kind: spec.kind, uiMover: spec.uiMover };
  const format = (v: number): string => formatValue(t, lane.prop, v, pctx);
  const kind = lane.prop === 'color' ? 'color' : lane.prop === 'text' ? 'text' : 'number';
  const [b0, b1] = axis.range;
  const level = spec.level;
  const speedMode = store.doc.pkg.speedMode;

  const curves: MotionCurveLayer[] = [];
  let sumCurve: SampledCurve | null = null;
  if (kind === 'number' && engine !== null) {
    const layered = isLayered(lane.prop);
    if (lane.prop === 'speed') {
      if (track !== undefined || spec.sumView)
        curves.push({
          curve: sampleCurve(session, engine, ref, spec.sumView ? 'sum' : 'guide', axis, compact),
          style: 'guide',
        });
      curves.push({ curve: sampleCurve(session, engine, ref, 'effective', axis, compact), style: 'main' });
    } else if (spec.sumView) {
      curves.push({ curve: sampleCurve(session, engine, ref, 'sum', axis, compact), style: 'main' });
    } else {
      const showSigma =
        layered &&
        spec.layersInUse.length > 1 &&
        !session.sigmaHidden.has(`${String(spec.line)}:${lane.prop}`) &&
        !compact;
      if (showSigma) curves.push({ curve: sampleCurve(session, engine, ref, 'sum', axis, compact), style: 'sigma' });
      curves.push({ curve: sampleCurve(session, engine, ref, 'main', axis, compact), style: 'main' });
    }
    if (lane.prop === 'alpha')
      sumCurve = spec.sumView ? (curves[0]?.curve ?? null) : sampleCurve(session, engine, ref, 'sum', axis, compact);
  }

  let range: ValueRange | null = null;
  if (kind === 'number') {
    range =
      session.frozenRanges.get(lk) ??
      session.lockedRanges.get(lk) ??
      stableRange(
        session.lastRanges.get(lk),
        autoRange(
          lane.prop,
          curves.map((c) => c.curve),
          keys,
          b0,
          b1,
        ),
      );
    session.lastRanges.set(lk, range);
  }

  const badges = new Map<KeyId, MotionSegmentBadge[]>();
  if (!compact) {
    const beats = keyBeats(keys);
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i] as MotionKeyView;
      const next = keys[i + 1] as MotionKeyView;
      if ((beats[i + 1] ?? 0) < b0 || k.beat > b1) continue;
      const list: MotionSegmentBadge[] = [];
      if (kind === 'text')
        list.push({ text: textModeBadge(t, k.textOut ?? '', next.textIn ?? '', k.ease), tone: 'info' });
      const eb = easeBadge(t, k.ease, level, false, inp.laneWidth < 110);
      if (eb !== null) list.push({ text: eb, tone: 'neutral' });
      if (lane.prop === 'speed' && speedMode === 'legacy') {
        const src = store.doc.keys.get(k.id)?.src;
        if (src?.ignoredEase !== undefined)
          list.push({ text: t(level === 'guided' ? m.badgeIgnoredGuided : m.badgeIgnored), tone: 'warning' });
        if (isEased(k.ease))
          list.push({ text: t(level === 'guided' ? m.badgeBakedGuided : m.badgeBaked), tone: 'baked' });
      }
      const approx = session.approx.get(k.id);
      if (approx !== undefined) {
        if (approx === k.ease)
          list.push({ text: t(level === 'guided' ? m.badgeApproxGuided : m.badgeApprox), tone: 'info' });
        else session.approx.delete(k.id);
      }
      if (list.length > 0) badges.set(k.id, list);
    }
  }

  let absentText: string | null = null;
  if (track === undefined) {
    absentText =
      isLayered(lane.prop) && ref.layer > 0
        ? t(m.layerEmpty, { n: ref.layer })
        : t(absentMessageKey(lane.prop, spec.kind, spec.uiMover));
  }

  let ghostKey: MotionGhostKey | null = null;
  const first = keys[0];
  if ((kind === 'color' || kind === 'text') && first !== undefined && first.beat > 0) {
    ghostKey =
      kind === 'color'
        ? { beat: 0, text: t(m.ghostDefaultWhite), rgb: WHITE }
        : { beat: 0, text: t(m.ghostDefaultText) };
  }

  const focus = editor.focusedColumn;
  const focused = focus !== null && focus.line === ref.line && focus.prop === ref.prop && focus.layer === ref.layer;

  return {
    key: lk,
    kind,
    label: lane.sub === null ? null : t(subLabelKey(lane.sub)),
    keys,
    range,
    curves,
    hidden: sumCurve !== null && engine !== null ? hiddenBands(engine, spec.line, sumCurve, b0, b1) : [],
    hiddenText: t(m.notesHidden),
    colorBands: kind === 'color' ? colorBands(engine, keys, axis) : [],
    format,
    formatText: (text) => quoteText(t, text),
    badges,
    absentText,
    ghostKey,
    raw: session.rawShown.has(lk) && engine !== null ? rawEvents(engine, ref, format, t) : null,
    negativeZone: lane.prop === 'alpha',
    valueLabels: !compact && inp.laneWidth >= 64,
    focused,
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Column scenes (cached)
// ---------------------------------------------------------------------------------------------------------------

interface SceneCacheEntry {
  readonly parts: readonly unknown[];
  readonly scene: MotionColumnScene;
}
const sceneCaches = new WeakMap<MotionSession, Map<string, SceneCacheEntry>>();

function sameParts(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((x, i) => Object.is(x, b[i]));
}

/** The scene of a column now (cached while its inputs are unchanged). */
export function columnScene(
  session: MotionSession,
  spec: ColumnSpec,
  axis: TimeAxis,
  widthPx: number,
  compact: boolean,
): MotionColumnScene {
  const store = session.services.store;
  const engine = session.services.engine;
  const editor = session.services.editor.getState();
  if (store === null || !store.doc.lines.has(spec.line)) {
    return {
      lanes: [],
      pairLinked: null,
      compact,
      message: session.services.i18n.t(store === null ? m.needProject : m.lineGone),
    };
  }
  const parts: unknown[] = [
    spec,
    store,
    store.version(lineTopic(spec.line)),
    store.version('bpm'),
    store.version('pkg'),
    engine,
    engine?.revision ?? -1,
    editor.selection,
    editor.focusedColumn,
    axis.range[0],
    axis.range[1],
    axis.pxPerBeat,
    widthPx,
    compact,
    session.version,
  ];
  let cache = sceneCaches.get(session);
  if (cache === undefined) {
    cache = new Map();
    sceneCaches.set(session, cache);
  }
  const hit = cache.get(spec.id);
  if (hit !== undefined && sameParts(hit.parts, parts)) return hit.scene;
  const n = Math.max(1, spec.lanes.length);
  const laneWidth = (widthPx - 6 * (n - 1)) / n;
  const lanes = spec.lanes.map((lane) =>
    buildLane({ session, store, engine, editor, spec, lane, axis, laneWidth, compact }),
  );
  const line = store.doc.lines.get(spec.line);
  const scene: MotionColumnScene = {
    lanes,
    pairLinked: spec.pair === 'position' ? (line?.editor.xyLinked ?? false) : null,
    compact,
    message: null,
  };
  cache.set(spec.id, { parts, scene });
  return scene;
}

/** Drops cached scenes (e.g. after the column set changed). */
export function dropSceneCache(session: MotionSession, keep?: ReadonlySet<string>): void {
  const cache = sceneCaches.get(session);
  if (cache === undefined) return;
  for (const id of [...cache.keys()]) if (keep === undefined || !keep.has(id)) cache.delete(id);
}

// ---------------------------------------------------------------------------------------------------------------
// Overlay scenes
// ---------------------------------------------------------------------------------------------------------------

/** Paste anchor beat (f64) for ghost drawing: the playhead or the pointer, snapped, plus the ghost shift. */
function pasteAnchor(session: MotionSession, axis: TimeAxis, shift: number): number {
  const s = session.services;
  const anchorSetting = s.settings.value.editing.pasteAnchor;
  const div = s.editor.getState().snap.div;
  const base = anchorSetting === 'playhead' ? playheadBeat(session) : (session.pointerBeat() ?? playheadBeat(session));
  return Beats.toF64(axis.snap(base)) + shift / Math.max(1, div);
}

export function columnOverlay(
  session: MotionSession,
  spec: ColumnSpec,
  axis: TimeAxis,
  scene: MotionColumnScene,
): MotionOverlayScene | null {
  const s = session.services;
  const store = s.store;
  if (store === null || scene.lanes.length === 0) return null;
  const editor = s.editor.getState();
  const t: Translate = (k, p) => s.i18n.t(k, p);
  const hover = session.hover?.instanceId === spec.id ? session.hover.hit : null;

  // RPE placement ghost.
  let placement: MotionOverlayScene['placement'] = null;
  const pl = editor.placement;
  if (pl?.kind === 'segment' && pl.line === spec.line) {
    const li = spec.lanes.findIndex(
      (l) => l.prop === pl.prop && (spec.sumView ? pl.layer === 0 : l.layer === pl.layer),
    );
    const lane = scene.lanes[li];
    if (li >= 0 && lane !== undefined) {
      const beat0 = Beats.toF64(pl.start);
      const pointer = session.pointerBeat();
      const beat1 = pointer !== null ? Beats.toF64(axis.snap(Math.max(0, pointer))) : beat0;
      const lo = Math.min(beat0, beat1);
      const hi = Math.max(beat0, beat1);
      // Keys in between (also on the linked X/Y partner, which gets the same segment) refuse the placement.
      const inside = keysInsideSpan(store.doc, store.idx, { line: pl.line, prop: pl.prop, layer: pl.layer }, lo, hi);
      const valid = beat1 > beat0 && !inside;
      placement = {
        lane: li,
        beat0,
        beat1,
        valid,
        constant: pl.constant,
        text: !valid
          ? t(beat1 <= beat0 ? m.placeTooShort : m.placeBlocked)
          : t(pl.constant ? m.placeStartHold : m.placeStart),
      };
    }
  }

  // Paste ghosts of keys (W2-03 runs the paste; the shared placement state says what is pending).
  const ghosts: Array<{ lane: number; points: Array<readonly [number, number]> }> = [];
  if (pl?.kind === 'paste') {
    const payload = pl.payload;
    const originF = Beats.toF64(payload.origin);
    const anchor = pl.mode.originalBeats ? originF : pasteAnchor(session, axis, pl.shift);
    for (const slice of payload.tracks) {
      const target = pl.mode.toOriginalLines
        ? (payload.lines[slice.lineSlot]?.sourceLine ?? pl.targetLine)
        : pl.targetLine;
      if (target !== spec.line) continue;
      const li = spec.lanes.findIndex(
        (l) => l.prop === slice.prop && (pl.mode.asNewLayer || spec.sumView || l.layer === slice.layer),
      );
      if (li < 0) continue;
      const negate = pl.mode.mirror && (slice.prop === 'moveX' || slice.prop === 'rotate');
      const points = slice.keys.map((k): readonly [number, number] => {
        const v = numericOf(k.vOut);
        return [anchor + Beats.toF64(k.t), negate && Number.isFinite(v) ? -v : v];
      });
      ghosts.push({ lane: li, points });
    }
  }

  // Value at the playhead.
  const playhead: Array<{ lane: number; beat: number; value: number }> = [];
  const engine = s.engine;
  const pb = axisContext(axis)?.playheadBeat;
  if (engine !== null && pb !== undefined && !scene.compact) {
    const sec = engine.tempo.secAtBeatF64(pb);
    spec.lanes.forEach((lane, li) => {
      if (scene.lanes[li]?.kind !== 'number') return;
      const value = laneValueAt(engine, spec, lane, sec);
      if (Number.isFinite(value)) playhead.push({ lane: li, beat: pb, value });
    });
  }

  const drag = session.drag?.instanceId === spec.id ? session.drag : null;
  return {
    hover,
    placement,
    ghosts,
    dragLabel: drag === null ? null : { lane: drag.lane, beat: drag.beat, value: drag.value, text: drag.text },
    playhead,
  };
}

const ONE = new Float32Array(1);

/** The value a lane shows at chart second `sec` (its layer, Σ in Guided sum views, effective speed for speed). */
export function laneValueAt(engine: Engine, spec: ColumnSpec, lane: LaneSpec, sec: number): number {
  try {
    if (lane.prop === 'speed') {
      sampleShownSpeed(engine, spec.line, sec, sec, ONE);
      return ONE[0] ?? Number.NaN;
    }
    return engine.channelValue(spec.line, lane.prop, sec, spec.sumView ? 'sum' : lane.layer);
  } catch {
    return Number.NaN;
  }
}

/** Ease of the segment that starts at `key` (for picker defaults). */
export function easeOfKey(store: DocStore, key: KeyId): Ease | null {
  return store.doc.keys.get(key)?.ease ?? null;
}
